import { periodRangeText } from '../format/periodLabel';
import { PeriodRow } from './practice.models';

export enum TrialBalanceType {
  Annual = 0,
  ManagementOnly = 1,
}

export enum TrialBalanceImportMode {
  Bookkeeping = 0,
  Csv = 1,
  Manual = 2,
  QuickBooks = 3,
  Xero = 4,
  FreeAgent = 5,
}

/** Statement areas used by the chart of accounts. */
export enum StatementArea {
  ProfitAndLoss = 0,
  BalanceSheet = 1,
  Equity = 2,
}

// ---- Trial balance ----

export interface TrialBalanceRow {
  id: string;
  refNo: string;
  fromDate: string;
  toDate: string;
  description: string;
  importMode: string;
  importModeId: number;
  type: string;
  typeId: number;
  status: string;
  isBalanced: boolean;
  totalDebit: number;
  totalCredit: number;
  lineCount: number;
  accountingPeriodId: string | null;
  periodLabel: string;
  label: string;
}

export interface TrialBalanceLineView {
  lineNo: number;
  accountCode: string;
  particular: string | null;
  debit: number;
  credit: number;
  statementArea: number | null;
}

export interface ChartAccountOption {
  code: string;
  name: string;
  accountType: string;
  statementArea: number;
}

export interface TrialBalanceDetail {
  id: string;
  clientId: string;
  clientName: string;
  refNo: string;
  description: string | null;
  typeId: number;
  importModeId: number;
  accountingPeriodId: string | null;
  periodLabel: string;
  fromDate: string | null;
  toDate: string | null;
  lines: TrialBalanceLineView[];
  totalDebit: number;
  totalCredit: number;
  difference: number;
  isBalanced: boolean;
  status: string;
  profit: number;
  isPosted: boolean;
  postedAtUtc: string | null;
  accounts: ChartAccountOption[];
  /** Issue #36: a report uses this trial balance, so its type (Annual / Management only) cannot be changed. */
  isUsedByReport?: boolean;
  /** Why a save was stored but not posted, when the server says (APT334: lines held on Import Exceptions, an identical import). */
  postMessage?: string | null;
}

export interface TrialBalanceLineInput {
  accountCode: string;
  particular: string | null;
  debit: number;
  credit: number;
  /**
   * BUG-065/067/068, on a line read from a file only: `translated` when an old-app account code was moved to the
   * current code (its account name confirmed it), `check` when the line has an old-app code but no name to confirm it.
   */
  legacyCodeStatus?: LegacyCodeStatus | null;
  /** The note shown on the line, e.g. "Old code 8150 translated to 8590." */
  legacyCodeNote?: string | null;
  /**
   * FRS import (14-Oct): TB profit - on a line read from a file for a client or brought by Import Balances (never sent back): the
   * statement area the accounts give the line (the chart's, or for a code the chart lacks its old 3.0 account's), null when the
   * accounts leave it unclassified.
   */
  statementArea?: number | null;
}

/** `notInChart`: the file's code (e.g. Xero 200) is not an active account in the client's chart. */
export type LegacyCodeStatus = 'translated' | 'check' | 'notInChart';

export interface SaveTrialBalancePayload {
  clientId: string;
  id: string | null;
  refNo: string;
  description: string | null;
  typeId: number;
  importModeId: number;
  accountingPeriodId: string | null;
  fromDate: string | null;
  toDate: string | null;
  /** Full replacement line set; null keeps the saved lines. */
  lines: TrialBalanceLineInput[] | null;
}

/** BUG-038: the accounting packages a trial balance can be imported from through their own login. */
export type AccountingProviderId = 'xero' | 'quickbooks' | 'freeagent';

/** Whether a provider can be used (the deployment is configured for it) and whether the client is connected to it. */
export interface AccountingConnectionStatus {
  provider: AccountingProviderId;
  providerName: string;
  configured: boolean;
  connected: boolean;
  connectedTo: string | null;
  /** Shown when `configured` is false, e.g. "Xero is not configured for this firm - ask your administrator." */
  message: string | null;
}

/** The answer to starting a connection: the provider's consent URL to open, or the saved connection to use as it is. */
export interface AccountingConnectResult {
  provider: AccountingProviderId;
  providerName: string;
  configured: boolean;
  connected: boolean;
  authorizeUrl: string | null;
  connectedTo: string | null;
  message: string | null;
}

/** What the provider's redirect carried back to the app (read from the /oauth/callback URL). */
export interface AccountingCallbackPayload {
  state: string;
  code: string | null;
  error: string | null;
  errorDescription: string | null;
  realmId: string | null;
}

export interface AccountingCallbackResult {
  provider: AccountingProviderId;
  providerName: string;
  clientId: string;
  connectedTo: string | null;
}

export interface CarryForwardPayload {
  sourceTrialBalanceId: string;
  broughtForwardOnly: boolean;
}

/** A line as edited on screen; amounts stay text so typing "12." is not lost. */
export interface TrialBalanceEditLine {
  accountCode: string;
  particular: string;
  debit: string;
  credit: string;
  /** BUG-065/067/068: the read file's old-app code note, until the line's account is changed. Never saved. */
  legacyCodeStatus?: LegacyCodeStatus;
  legacyCodeNote?: string;
  /**
   * FRS import (14-Oct): TB profit - the statement area the server gave the line (the area the accounts use; null when they leave
   * it unclassified), with the code and particular it classified. Used only while the line still has that code (see
   * `lineStatementArea`). Never saved.
   */
  statementArea?: number | null;
  classifiedCode?: string;
  classifiedParticular?: string;
}

/** The one model the create/edit trial balance page and its child panels share. */
export interface TrialBalanceEditModel {
  id: string | null;
  clientId: string;
  clientName: string;
  refNo: string;
  description: string;
  typeId: number;
  importModeId: number;
  accountingPeriodId: string | null;
  periodLabel: string;
  fromDate: any;
  toDate: any;
  lines: TrialBalanceEditLine[];
  accounts: ChartAccountOption[];
  totalDebit: number;
  totalCredit: number;
  difference: number;
  isBalanced: boolean;
  profit: number;
  isPosted: boolean;
  postedAtUtc: string | null;
  /** Issue #36: see TrialBalanceDetail - the Type is locked while a report uses this trial balance. */
  isUsedByReport?: boolean;
}

/**
 * BUG-037: the limits the server holds a trial balance's details to (TrialBalanceScreenRules) - shared with other Capium
 * modules, so as the old application: a Ref. No. of at most 10 characters (TB-1, TB-2 ...) and a one-line Description
 * of at most 50.
 */
export const TB_REF_MAX_LENGTH = 10;
export const TB_DESCRIPTION_MAX_LENGTH = 50;

/**
 * BUG-037: the details confirmed in Create Trial Balance. Nothing is saved from them alone - the editor creates the
 * trial balance with them when its first account line is saved, so an abandoned one never appears in the list.
 */
export interface TrialBalanceDraftHeader {
  refNo: string;
  description: string | null;
  typeId: number;
  importModeId: number;
  accountingPeriodId: string | null;
  periodLabel: string;
  fromDate: string | null;
  toDate: string | null;
  /**
   * BUG-036: the lines read from the file chosen in Create Trial Balance (CSV, or exported from QuickBooks, Xero or
   * FreeAgent). They open in the grid unsaved; the editor's Save stores them once they balance.
   */
  lines?: TrialBalanceLineInput[];
}

/** BUG-037: the router state key the confirmed details travel to the editor under. */
export const TB_DRAFT_STATE = 'trialBalanceDraft';

export interface PeriodOption {
  id: string;
  label: string;
  periodFrom: string;
  periodTo: string;
}

// ---- Chart of accounts ----

export interface AccountTypeOption {
  id: number;
  name: string;
  groupName: string;
  statementArea: number;
  /** The client types (0 limited, 1 sole trader, 2 partnership, 3 LLP) that may use it; absent when every type may. */
  clientTypes?: number[] | null;
}

export interface ChartOfAccountsOptions {
  accountTypes: AccountTypeOption[];
  vatCodes: string[];
  /** The type of the client the options were asked for (`getAccountOptions(clientId)`). */
  clientType?: number | null;
}

export interface ChartAccountRow {
  id: string;
  accountName: string;
  category: string;
  group: string;
  code: string;
  accountTypeId: number;
  vatCode: string | null;
  isActive: boolean;
  /** Archived accounts are listed under the Archived status only. */
  isArchived?: boolean;
  /** APT369: the code is in Capium's default chart for the client's type (the screen colours it); a custom code is false. */
  isStandard?: boolean;
}

export interface ChartAccountQuery {
  search: string | null;
  /** -1 all (archived accounts left out), 1 active, 0 inactive, 2 archived. */
  status: number;
  accountTypeId: number | null;
}

export interface SaveChartAccountPayload {
  clientId: string;
  id: string | null;
  accountTypeId: number;
  accountName: string;
  accountCode: string;
  vatCode: string | null;
  isActive: boolean;
}

export interface ChartAccountExportQuery extends ChartAccountQuery {
  format: number;
  isPrint: boolean;
  ids: string[];
}

// ---- Helpers ----

const amountFormat = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Formats an amount as "£ 12,400.40"; zero shows as "-" when dashForZero is set. */
export function formatGbp(value: number | null | undefined, dashForZero = false): string {
  const amount = Number(value) || 0;
  if (dashForZero && Math.abs(amount) < 0.005) {
    return '-';
  }
  return `${amount < 0 ? '-' : ''}£ ${amountFormat.format(Math.abs(amount))}`;
}

/** Reads a typed amount ("1,200.50", "£ 30") as a number; anything unreadable is 0. */
export function parseAmount(value: any): number {
  if (typeof value === 'number') {
    return isFinite(value) ? value : 0;
  }
  if (value === null || value === undefined) {
    return 0;
  }
  const cleaned = `${value}`.replace(/[£,\s]/g, '');
  const parsed = Number(cleaned);
  return cleaned.length > 0 && isFinite(parsed) ? parsed : 0;
}

/**
 * BUG-039: an amount in pounds and pence, rounded half away from zero exactly as the server stores it
 * (TrialBalanceScreenRules.RoundAmount): 1000.555 is 1000.56, -0.005 is -0.01. Worked on the decimal text, not
 * `value * 100`, so binary floating point cannot turn 1000.555 into 1000.55.
 */
export function roundAmount(value: number): number {
  if (!isFinite(value) || value === 0) {
    return 0;
  }
  const text = `${Math.abs(value)}`;
  // Numbers too small or large for plain decimal text (1e-7, 1e21) cannot carry a half-penny tie.
  const rounded = text.includes('e') ? Math.round(Math.abs(value) * 100) : Math.round(Number(`${text}e2`));
  return Math.sign(value) * (rounded / 100);
}

/**
 * BUG-039: a line's amounts as the server stores them (TrialBalanceScreenRules.Normalise): each side rounded to
 * pence, then their net on one side. A grid line can show a negative amount where it was typed (BUG-039, as the old
 * grid) but never both a debit and a credit (typing on one side clears the other); it is saved and totalled as this
 * net, so the server stores it on one side. The line's debit-less-credit is unchanged, so are the totals' difference
 * and the balance (BUG-036).
 */
export function normaliseAmounts(debit: unknown, credit: unknown): { debit: number; credit: number } {
  const net = roundAmount(roundAmount(parseAmount(debit)) - roundAmount(parseAmount(credit)));
  return net >= 0 ? { debit: net, credit: 0 } : { debit: 0, credit: -net };
}

/**
 * BUG-039: what a Debit or Credit box keeps while it is typed in, as the old application's grid: digits and at most
 * one "." (any decimals are kept while typing and rounded to two when the box is left, so 1000.555 becomes 1000.56 as it did in the old grid). A leading minus sign is kept, and leaving the box keeps it on its side
 * (-500 shows as -500.00, `normaliseEditLine`); the line is saved as its net, a negative debit as a credit. Thousands commas and a "£" may be pasted; leaving the box shows the amount
 * without them.
 */
export function sanitiseAmountInput(value: string | null | undefined): string {
  const raw = `${value ?? ''}`;
  const sign = /^\s*-/.test(raw) ? '-' : '';
  const text = raw.replace(/[^0-9.,£\s]/g, '');
  const dot = text.indexOf('.');
  if (dot < 0) {
    return `${sign}${text}`;
  }
  const decimals = text.slice(dot + 1).replace(/[^0-9]/g, '');
  return `${sign}${text.slice(0, dot)}.${decimals}`;
}

/** BUG-039: an amount as the grid shows it - always two decimals ("50000.00", "0.00"). */
export function formatAmount(value: number): string {
  return roundAmount(value).toFixed(2);
}

/**
 * BUG-039: what a line's Debit and Credit boxes show once an amount has been entered - two decimals, the other side as
 * 0.00. A negative amount stays where it was typed (BUG-039, QA round 2: as the old grid, -500 is shown as -500.00);
 * the line is saved and totalled as its net (`toLineInputs`, `recalculateTrialBalance`), so the server still stores it
 * on one side. A line given both a debit and a credit shows their net. A line with no amount typed keeps both boxes empty.
 */
export function normaliseEditLine(line: TrialBalanceEditLine): void {
  if (!`${line.debit ?? ''}`.trim() && !`${line.credit ?? ''}`.trim()) {
    return;
  }
  const debit = roundAmount(parseAmount(line.debit));
  const credit = roundAmount(parseAmount(line.credit));
  if (debit !== 0 && credit !== 0) {
    const amounts = normaliseAmounts(debit, credit);
    line.debit = formatAmount(amounts.debit);
    line.credit = formatAmount(amounts.credit);
    return;
  }
  line.debit = formatAmount(debit);
  line.credit = formatAmount(credit);
}

export function emptyEditLine(): TrialBalanceEditLine {
  return { accountCode: '', particular: '', debit: '', credit: '' };
}

export function isEmptyEditLine(line: TrialBalanceEditLine): boolean {
  return ![line.accountCode, line.particular, line.debit, line.credit].some((v) => `${v ?? ''}`.trim().length > 0);
}

/** A trial balance grid always offers at least this many lines. */
export const MIN_TRIAL_BALANCE_ROWS = 8;

/** Keeps at least MIN_TRIAL_BALANCE_ROWS rows and exactly one trailing empty row to type into. */
export function ensureTrailingRows(lines: TrialBalanceEditLine[]): TrialBalanceEditLine[] {
  const result = [...lines];
  if (result.length === 0 || !isEmptyEditLine(result[result.length - 1])) {
    result.push(emptyEditLine());
  }
  while (result.length < MIN_TRIAL_BALANCE_ROWS) {
    result.push(emptyEditLine());
  }
  return result;
}

export function findAccount(
  accounts: ChartAccountOption[],
  code: string | null | undefined,
): ChartAccountOption | undefined {
  const key = (code ?? '').trim().toUpperCase();
  return key ? accounts.find((a) => a.code.trim().toUpperCase() === key) : undefined;
}

function sameText(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toUpperCase() === (b ?? '').trim().toUpperCase();
}

/**
 * FRS import (14-Oct): TB profit - the statement area a line counts under, as the accounts count it. The server classifies each
 * line it returns (getTrialBalance, readFile, Import Balances) as the accounts do: a code the client's chart lacks (an old 3.0
 * code kept on import, e.g. 1580 Donations) by its 3.0 account, and (APT218) a 3.0 account on a code the chart gives another
 * account - 3.0's 3000 UK Corporation Tax on the default chart's 3000 Ordinary Share Capital - as that 3.0 account. Counting only
 * the chart's accounts showed the FRS102 1A test client a profit of 233,000 against the accounts' 88,800.
 *
 * The server's area is used only while the line still has the code it classified (an edited code reads the chart again); on a
 * code the chart has, also only while the particular is the one it classified - the 3.0 account is told by that name - and never
 * in place of the chart's account when the server could not place the line. Otherwise the chart's account decides, and a line
 * neither places is unclassified (null).
 */
export function lineStatementArea(
  model: Pick<TrialBalanceEditModel, 'accounts'>,
  line: TrialBalanceEditLine,
): number | null {
  const account = findAccount(model.accounts ?? [], line.accountCode);
  const classified =
    line.classifiedCode !== undefined && line.accountCode?.trim() && sameText(line.classifiedCode, line.accountCode)
      ? line.statementArea
      : undefined;
  if (
    classified !== undefined &&
    classified !== null &&
    (!account || sameText(line.classifiedParticular, line.particular))
  ) {
    return classified;
  }
  return account ? account.statementArea : null;
}

/** FRS import (14-Oct): TB profit - the user chose the line's account, so the server's classification of it no longer applies. */
export function forgetLineClassification(line: TrialBalanceEditLine): void {
  delete line.statementArea;
  delete line.classifiedCode;
  delete line.classifiedParticular;
}

/** FRS import (14-Oct): TB profit - keeps the server's area for a line, with the code and particular it classified. */
export function setLineClassification(
  line: TrialBalanceEditLine,
  classified: {
    accountCode: string | null | undefined;
    particular: string | null | undefined;
    statementArea?: number | null;
  },
): void {
  if (classified.statementArea === undefined || !classified.accountCode?.trim()) {
    forgetLineClassification(line);
    return;
  }
  line.statementArea = classified.statementArea;
  line.classifiedCode = classified.accountCode;
  line.classifiedParticular = classified.particular ?? '';
}

/** Recomputes totals, balance and profit from the lines on screen. */
export function recalculateTrialBalance(model: TrialBalanceEditModel): void {
  let debit = 0;
  let credit = 0;
  let profitAndLoss = 0;
  for (const line of model.lines) {
    if (!line.accountCode?.trim()) {
      continue;
    }
    // BUG-039: each line counts as it is stored - in pence, on one side - so the totals are the server's.
    const { debit: d, credit: c } = normaliseAmounts(line.debit, line.credit);
    debit += d;
    credit += c;
    // FRS import (14-Oct): TB profit - the area the accounts give the line, not only the chart's.
    if (lineStatementArea(model, line) === StatementArea.ProfitAndLoss) {
      profitAndLoss += c - d;
    }
  }
  model.totalDebit = round2(debit);
  model.totalCredit = round2(credit);
  model.difference = round2(debit - credit);
  model.isBalanced = Math.abs(debit - credit) < 0.005;
  // ap5-a6 (item 120): this used to fall back to `credit - debit` whenever no line resolved to a
  // chart account, which is the out-of-balance difference, not a profit - so a single mistyped code
  // turned the figure into nonsense with no warning. The profit follows the account types or it is
  // nil; `profitIsClassified` tells the screen whether any P&L code was actually recognised.
  model.profit = round2(profitAndLoss);
}

/**
 * True when at least one line is in the profit and loss - by its chart account, or (FRS import (14-Oct): TB profit) by the area
 * the server gave it (`lineStatementArea`).
 */
export function hasClassifiedProfitLines(model: TrialBalanceEditModel): boolean {
  return model.lines.some((line) => {
    if (!line.accountCode?.trim()) {
      return false;
    }
    return lineStatementArea(model, line) === StatementArea.ProfitAndLoss;
  });
}

/**
 * ap5-a6 (item 121): a loss reads as a loss. The stored figure keeps its sign (credit less debit on
 * the profit and loss codes), but the screen shows "Profit"/"Loss" against a positive amount.
 */
export function profitOrLossLabel(profit: number | null | undefined): string {
  return (Number(profit) || 0) < 0 ? 'Loss' : 'Profit';
}

export function profitOrLossText(profit: number | null | undefined): string {
  return formatGbp(Math.abs(Number(profit) || 0));
}

export function linesWithCode(model: TrialBalanceEditModel): TrialBalanceEditLine[] {
  return model.lines.filter((l) => !!l.accountCode?.trim());
}

export function toLineInputs(lines: TrialBalanceEditLine[]): TrialBalanceLineInput[] {
  return lines
    .filter((l) => !!l.accountCode?.trim())
    .map((l) => ({
      accountCode: l.accountCode.trim(),
      particular: l.particular?.trim() ? l.particular.trim() : null,
      // BUG-039: sent as the server stores them, so what was shown is what is saved.
      ...normaliseAmounts(l.debit, l.credit),
    }));
}

/**
 * BUG-039: a saved line (or one read from a file or carried forward, BUG-036) shows its amounts with two decimals,
 * the side it does not use as 0.00.
 */
export function toEditLines<T extends Pick<TrialBalanceLineView, 'accountCode' | 'particular' | 'debit' | 'credit'>>(
  lines: T[],
): TrialBalanceEditLine[] {
  return lines.map((l) => {
    const line: TrialBalanceEditLine = {
      accountCode: l.accountCode ?? '',
      particular: l.particular ?? '',
      debit: l.debit ? `${l.debit}` : '',
      credit: l.credit ? `${l.credit}` : '',
    };
    if (line.accountCode.trim()) {
      const amounts = normaliseAmounts(l.debit, l.credit);
      line.debit = formatAmount(amounts.debit);
      line.credit = formatAmount(amounts.credit);
    }
    const legacy = l as Partial<Pick<TrialBalanceLineInput, 'legacyCodeStatus' | 'legacyCodeNote' | 'statementArea'>>;
    if (legacy.legacyCodeStatus && legacy.legacyCodeNote) {
      line.legacyCodeStatus = legacy.legacyCodeStatus;
      line.legacyCodeNote = legacy.legacyCodeNote;
    }
    // FRS import (14-Oct): TB profit - the area the server gave a saved or read line (absent on a line it did not classify).
    if (legacy.statementArea !== undefined && line.accountCode.trim()) {
      setLineClassification(line, {
        accountCode: line.accountCode,
        particular: line.particular,
        statementArea: legacy.statementArea,
      });
    }
    return line;
  });
}

export function toPeriodOptions(periods: PeriodRow[]): PeriodOption[] {
  return [...periods]
    .sort((a, b) => `${b.periodTo}`.localeCompare(`${a.periodTo}`))
    .map((p) => ({ id: p.id, label: periodRangeText(p.fromDate, p.toDate, ' to '), periodFrom: p.periodFrom, periodTo: p.periodTo }));
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
